import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { db } from '../firebase/firebase';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import QRCode from 'qrcode';
import WIcon from '../components/shared/WIcon';
import Modal from '../components/shared/Modal';
import '../styles/Dashboard.css';

const TIER_LABELS = ['', 'All Staff', 'Manager', 'Executive'];
const TIER_COLORS = ['', '#10b981', '#009950', '#f59e0b'];
const TIER_BG = ['', 'rgba(16,185,129,.10)', 'rgba(0,191,99,.10)', 'rgba(245,158,11,.10)'];

const SUPER_COLOR = '#6366f1';
const SUPER_BG = 'rgba(99,102,241,.10)';

const isSuperTier = (tier) => String(tier).toLowerCase() === 'super';

const initialsFor = (name, surname) => {
  const parts = [name, surname].filter(Boolean);
  if (parts.length === 0) return '—';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
};

const TierBadge = ({ tier }) => {
  if (isSuperTier(tier)) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px', borderRadius: 999,
        background: SUPER_BG, color: SUPER_COLOR,
        fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
      }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: SUPER_COLOR }} />
        Super User
      </span>
    );
  }
  const t = Number(tier) || 1;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 999,
      background: TIER_BG[t], color: TIER_COLORS[t],
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: TIER_COLORS[t] }} />
      T{t} · {TIER_LABELS[t]}
    </span>
  );
};

const generateQRCode = async (employeeId) => {
  try {
    const qrCodeUrl = await QRCode.toDataURL(`benetica:user:${employeeId}`);
    await updateDoc(doc(db, 'users', employeeId), { qrCodeUrl });
    return qrCodeUrl;
  } catch (err) {
    console.error('QR generation failed:', err);
    return null;
  }
};

const EmployeeFormModal = ({ initialData, mode, companies, onClose, onSaved }) => {
  const isEdit = mode === 'edit';
  const [formData, setFormData] = useState(initialData || {
    name: '', surname: '', phone: '', companyId: companies[0]?.id || '', tier: '1',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const setField = (k) => (e) => {
    setFormData((f) => ({ ...f, [k]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone,
        companyId: formData.companyId,
        tier: formData.tier,
        ...(isEdit ? {} : { createdAt: new Date() }),
      };

      let employeeId;
      if (isEdit) {
        await updateDoc(doc(db, 'users', initialData.id), payload);
        employeeId = initialData.id;
      } else {
        const ref = await addDoc(collection(db, 'users'), payload);
        employeeId = ref.id;
      }
      await generateQRCode(employeeId);
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={isEdit ? 'Edit Employee' : 'Add Employee'} onClose={onClose} width={460}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="loysta-alert error">{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>First name</label>
            <input type="text" className="form-control" value={formData.name} onChange={setField('name')} placeholder="Alex" required />
          </div>
          <div className="form-group">
            <label>Last name</label>
            <input type="text" className="form-control" value={formData.surname} onChange={setField('surname')} placeholder="Chen" required />
          </div>
        </div>

        <div className="form-group">
          <label>Phone</label>
          <input type="tel" className="form-control" value={formData.phone} onChange={setField('phone')} placeholder="+995 5XX XXX XXX" />
        </div>

        <div className="form-group">
          <label>Company {String(formData.tier).toLowerCase() === 'super' ? '' : '*'}</label>
          <select
            className="form-control"
            value={formData.companyId}
            onChange={setField('companyId')}
            required={String(formData.tier).toLowerCase() !== 'super'}
          >
            <option value="">{String(formData.tier).toLowerCase() === 'super' ? 'No company (super user)' : 'Select company'}</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>Benefit tier</label>
          <select className="form-control" value={formData.tier} onChange={setField('tier')}>
            <option value="1">Tier 1 — All Staff</option>
            <option value="2">Tier 2 — Manager</option>
            <option value="3">Tier 3 — Executive</option>
            <option value="super">Super User — sees every offer</option>
          </select>
          {String(formData.tier).toLowerCase() === 'super' && (
            <small style={{ color: '#64748b', fontSize: 12, marginTop: 4, display: 'block' }}>
              Super users bypass company and tier filters in the employee app — used for internal QA.
            </small>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button type="button" className="cancel-btn" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting} style={{ flex: 1, justifyContent: 'center' }}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add Employee'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const Employees = () => {
  const { registerPageContext } = useOutletContext();
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [modalState, setModalState] = useState({ open: false, mode: 'add', initialData: null });

  const fetchAll = useCallback(async () => {
    try {
      const [empSnap, compSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'companies')),
      ]);
      setEmployees(empSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setCompanies(compSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    registerPageContext({
      addLabel: 'Add Employee',
      onAdd: () => setModalState({ open: true, mode: 'add', initialData: null }),
    });
  }, [registerPageContext]);

  const handleEdit = (emp) => {
    setModalState({
      open: true,
      mode: 'edit',
      initialData: {
        id: emp.id,
        name: emp.name || '',
        surname: emp.surname || '',
        phone: emp.phone || '',
        companyId: emp.companyId || '',
        tier: String(emp.tier || '1'),
      },
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    try { await deleteDoc(doc(db, 'users', id)); fetchAll(); }
    catch (err) { console.error(err); }
  };

  const companyMap = Object.fromEntries(companies.map((c) => [c.id, c]));
  const filtered = employees.filter((e) => {
    if (tierFilter !== 'all' && String(e.tier) !== tierFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (e.name || '').toLowerCase().includes(s) ||
      (e.surname || '').toLowerCase().includes(s) ||
      (e.phone || '').toLowerCase().includes(s) ||
      (companyMap[e.companyId]?.name || '').toLowerCase().includes(s)
    );
  });

  const tierFilters = [
    { id: 'all',   label: 'All' },
    { id: '1',     label: 'Tier 1' },
    { id: '2',     label: 'Tier 2' },
    { id: '3',     label: 'Tier 3' },
    { id: 'super', label: 'Super' },
  ];

  return (
    <>
      {modalState.open && (
        <EmployeeFormModal
          mode={modalState.mode}
          initialData={modalState.initialData}
          companies={companies}
          onClose={() => setModalState({ open: false, mode: 'add', initialData: null })}
          onSaved={fetchAll}
        />
      )}

      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 12px' }}>
            <WIcon name="search" size={15} color="#94a3b8" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employees…"
              style={{ border: 'none', background: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', width: '100%' }}
            />
          </div>
          {tierFilters.map((t) => {
            const active = tierFilter === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTierFilter(t.id)}
                type="button"
                style={{
                  padding: '7px 14px',
                  borderRadius: 7,
                  border: `1.5px solid ${active ? '#00bf63' : '#e2e8f0'}`,
                  background: active ? '#00bf63' : 'white',
                  color: active ? 'white' : '#334155',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >{t.label}</button>
            );
          })}
          <button
            type="button"
            onClick={() => setModalState({ open: true, mode: 'add', initialData: null })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: '#00bf63', color: 'white',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              cursor: 'pointer', transition: 'background 0.15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#009950'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#00bf63'; }}
          >
            <WIcon name="plus" size={14} color="white" />
            Add Employee
          </button>
        </div>

        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Phone</th>
              <th>Company</th>
              <th>Tier</th>
              <th>QR</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading employees…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                {employees.length === 0 ? 'No employees yet. Click "Add Employee" to create one.' : 'No employees match your filters.'}
              </td></tr>
            ) : filtered.map((e) => {
              const company = companyMap[e.companyId];
              const isSuper = isSuperTier(e.tier);
              const t = Number(e.tier) || 1;
              const avatarGradient = isSuper
                ? 'linear-gradient(135deg, #6366f1, #a855f7)'
                : `linear-gradient(135deg, ${TIER_COLORS[t]}, ${t === 3 ? '#fbbf24' : t === 2 ? '#c1ff72' : '#6ee7b7'})`;
              return (
                <tr key={e.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: avatarGradient,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: isSuper ? 'white' : '#0f172a', flexShrink: 0,
                      }}>{initialsFor(e.name, e.surname)}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{e.name} {e.surname}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ color: '#334155' }}>{e.phone || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={{ color: '#64748b' }}>{company?.name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td><TierBadge tier={isSuper ? 'super' : t} /></td>
                  <td>
                    {e.qrCodeUrl ? (
                      <img src={e.qrCodeUrl} alt="QR" style={{ width: 32, height: 32, borderRadius: 4, border: '1px solid #e2e8f0' }} />
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button className="loysta-icon-btn" onClick={() => handleEdit(e)} title="Edit"><WIcon name="edit" size={15} /></button>
                      <button className="loysta-icon-btn danger" onClick={() => handleDelete(e.id)} title="Delete"><WIcon name="trash" size={15} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Employees;
